// One board row per active ED visit, derived from encounter state. Pure.
// CLAUDE.md hard rule 3: no value here is calculated or scored. Time in department is shown by
// the board from arrivedAt; timers come from the site rules (@diagnuvo/rules, L4.1) and alerts
// from the rules engine's events. The board counts down to breachAt itself.

import { z } from 'zod';
import type { EncounterState } from './encounter.ts';
import { complaintSchema, locationSchema, triageSchema } from './events.ts';

const count = z.number().int().nonnegative();

export const boardTimerSchema = z.object({
  id: z.string().min(1),
  label: z.string(),
  startedAt: z.string(),
  warnAt: z.string(),
  breachAt: z.string(),
  stoppedAt: z.string().optional(),
  /** The alert the rules engine recorded, as of its last event. */
  alert: z.enum(['none', 'warning', 'breach']),
  /** The alert's raise-and-clear round, sent back with an acknowledgement (L4.2). */
  cycle: z.number().int().nonnegative().optional(),
  /** When staff acknowledged the alert at its current level (AlertAcknowledged). */
  acknowledgedAt: z.string().optional(),
  /** The latest escalation step recorded for the current level (AlertEscalated). */
  escalation: z
    .object({ step: z.number().int().positive(), notify: z.string(), at: z.string() })
    .optional(),
});
export type BoardTimer = z.infer<typeof boardTimerSchema>;

export const boardEntrySchema = z.object({
  encounterId: z.string().min(1),
  siteId: z.string().min(1),
  status: z.enum(['arrived', 'admitted']),
  location: locationSchema.optional(),
  arrivedAt: z.string().optional(),
  admittedAt: z.string().optional(),
  patient: z
    .object({
      mrn: z.string().optional(),
      nhsNumber: z.string().optional(),
      family: z.string().optional(),
      given: z.string().optional(),
      birthDate: z.string().optional(),
      gender: z.string().optional(),
    })
    .optional(),
  labs: z.object({ pending: count, resulted: count }),
  imaging: z.object({ pending: count, reported: count }),
  latestVitalsAt: z.string().optional(),
  lastEventAt: z.string().optional(),
  /** The presenting complaint and the latest triage category, as the source systems sent them. */
  complaint: complaintSchema.optional(),
  triage: triageSchema.extend({ at: z.string() }).optional(),
  /** How many recorded milestones the visit has reached (EncounterState.milestones), 1 to 4. */
  milestones: z.number().int().min(1).max(4).optional(),
  timers: z.array(boardTimerSchema).default([]),
});
export type BoardEntry = z.infer<typeof boardEntrySchema>;

/** Visits that belong on the ED board: arrived or admitted, and still in the ED. */
export function isOnBoard(state: EncounterState): boolean {
  return (
    (state.status === 'arrived' || state.status === 'admitted') && state.leftEdAt === undefined
  );
}

/**
 * The board row for an active visit, or undefined when it does not belong on the board.
 * `timers` from the site rules (@diagnuvo/rules boardTimers).
 */
export function boardEntry(
  siteId: string,
  encounterId: string,
  state: EncounterState,
  timers: readonly BoardTimer[] = [],
): BoardEntry | undefined {
  if (!isOnBoard(state) || (state.status !== 'arrived' && state.status !== 'admitted')) {
    return undefined;
  }
  const labs = Object.values(state.labs).filter((l) => l.status !== 'cancelled');
  const imaging = Object.values(state.imaging).filter((i) => i.status !== 'cancelled');
  return {
    encounterId,
    siteId,
    status: state.status,
    ...(state.location !== undefined && { location: state.location }),
    ...(state.arrivedAt !== undefined && { arrivedAt: state.arrivedAt }),
    ...(state.admittedAt !== undefined && { admittedAt: state.admittedAt }),
    ...(state.patient !== undefined && { patient: state.patient }),
    labs: {
      pending: labs.filter(
        (l) => l.status === 'ordered' || l.status === 'received' || l.status === 'preliminary',
      ).length,
      resulted: labs.filter((l) => l.status === 'final' || l.status === 'corrected').length,
    },
    imaging: {
      pending: imaging.filter((i) => !i.status.startsWith('reported')).length,
      reported: imaging.filter((i) => i.status.startsWith('reported')).length,
    },
    ...(state.latestVitalsAt !== undefined && { latestVitalsAt: state.latestVitalsAt }),
    ...(state.lastEventAt !== undefined && { lastEventAt: state.lastEventAt }),
    ...(state.complaint !== undefined && { complaint: state.complaint }),
    ...(state.triage !== undefined && { triage: state.triage }),
    ...(state.milestones !== undefined &&
      state.milestones.length > 0 && { milestones: state.milestones.length }),
    timers: [...timers],
  };
}
